// The WebGPU monarch's wing motion: the pose each frame draws, which chases a target at some
// rate. The engine's beats set the target: a flap's stroke, a flutter's quick strokes, or
// rest, where the wings just breathe.

import { WING_DEFAULTS, type WingStats } from "@bfly/engine";
import { clamp01 } from "@bfly/engine/math";

type MonarchMode = "rest" | "flap";

/** One frame's wing pose, in radians, and the body's lift in model units. */
export interface MonarchPose {
  // Each wing's angle around its hinge. Larger is more closed.
  foreFold: number;
  hindFold: number;
  // How far each wing bends toward its tip (the angle grows with the span squared).
  foreFlex: number;
  hindFlex: number;
  // Each wing's turn in its own plane, back from the body's axis.
  foreSweep: number;
  hindSweep: number;
  bodyLift: number;
}

interface MonarchTarget extends MonarchPose {
  // How fast the pose follows this target, per second (`FOLLOW`).
  followRate: number;
}

type MonarchBeat = Pick<WingStats, "beatOn" | "beatStart" | "beatDur" | "kind">;

interface FlutterBurst {
  start: number;
  duration: number;
  limits: number[];
}

// A flap's fold angles, in radians. Each wing swings from its open angle through its depth
// at the stroke's peak, and the hindwing trails the forewing by a fraction of the stroke.
const FLAP_FOLD = {
  foreOpen: 1.555,
  foreDepth: 1.95,
  hindOpen: 1.545,
  hindDepth: 1.9,
  hindLag: 0.07,
} as const;
// Everything else about a flap:
// - `ease`: the stroke's easing (an exponent under 1 hits the peak early).
// - The wings flex through one damped cycle, the hindwing by a fraction of the forewing's.
// - Both sweep toward the body at the peak.
// - The body lifts until `liftRise` of the way through the stroke, then settles.
const FLAP_SWING = {
  ease: 0.55,
  flex: 0.38,
  flexDamp: 0.3,
  hindFlexShare: 0.85,
  peakSweep: 0.25,
  lift: 0.06,
  liftRise: 0.55,
  liftSettle: 3,
} as const;
// At rest, the forewing breathes (two slow sines that only ever close it, in radians and
// radians per second), twitches once per cycle (in seconds), and both wings flex a little.
const REST_SWING = {
  breathe: 0.03,
  breatheRate: 0.85,
  ripple: 0.012,
  rippleRate: 2.27,
  ripplePhase: 1.3,
  twitch: 0.045,
  twitchCycle: 7.3,
  twitchLength: 0.5,
  twitchDecay: 4,
  foreFlex: 0.012,
  hindFlex: 0.01,
  flexRate: 1.7,
  hindFlexPhase: 0.5,
} as const;
// A flutter: two to four quick strokes, each 6 to 12 degrees deep, with the tips flexing
// against them.
const FLUTTER = {
  minStrokes: 2,
  strokeChoices: 3,
  minDepthDegrees: 6,
  depthRangeDegrees: 6,
  foreFlex: 0.12,
  hindFlex: 0.1,
} as const;
// How fast the pose follows its target, per second (each step's alpha is 1 - e^(-dt rate)):
// quick for a flap, slower for a flutter, and slow at rest, where it breathes.
const FOLLOW = { flap: 34, flutter: 22, rest: 6, bob: 22 } as const;

const SWEEP_FORE = 0.82;
const SWEEP_HIND = 0.6;
// The fitted rest outline leaves 23.7 degrees between each wing and fully closed.
const MAX_FOLD = (66.3 * Math.PI) / 180;
// At rest the hindwing stays this much more open than the forewing, so they don't cross.
const HIND_REST_GAP = 0.01;
const REST: MonarchPose = {
  foreFold: MAX_FOLD,
  hindFold: MAX_FOLD - HIND_REST_GAP,
  foreFlex: 0,
  hindFlex: 0,
  foreSweep: SWEEP_FORE,
  hindSweep: SWEEP_HIND,
  bodyLift: 0,
};
const FLAP_SECONDS = 0.75;

export function monarchTargets(t: number, mode: MonarchMode, start: number): MonarchTarget {
  if (mode === "flap" && t >= start && t - start < FLAP_SECONDS) {
    const u = (t - start) / FLAP_SECONDS;
    const shape = (x: number) => Math.sin(Math.PI * Math.pow(clamp01(x), FLAP_SWING.ease));
    const flex = FLAP_SWING.flex * Math.sin(2 * Math.PI * u) * (1 - FLAP_SWING.flexDamp * u);
    const lift =
      FLAP_SWING.lift *
      Math.sin(Math.PI * Math.min(u / FLAP_SWING.liftRise, 1)) *
      Math.exp(-FLAP_SWING.liftSettle * Math.max(0, u - FLAP_SWING.liftRise));
    return {
      foreFold: Math.min(REST.foreFold, FLAP_FOLD.foreOpen - FLAP_FOLD.foreDepth * shape(u)),
      hindFold: Math.min(
        REST.hindFold,
        FLAP_FOLD.hindOpen - FLAP_FOLD.hindDepth * shape(u - FLAP_FOLD.hindLag),
      ),
      foreFlex: flex,
      hindFlex: flex * FLAP_SWING.hindFlexShare,
      foreSweep: SWEEP_FORE + (FLAP_SWING.peakSweep - SWEEP_FORE) * shape(u),
      hindSweep: SWEEP_HIND + (FLAP_SWING.peakSweep - SWEEP_HIND) * shape(u),
      bodyLift: lift,
      followRate: FOLLOW.flap,
    };
  }
  const breathe =
    REST_SWING.breathe * Math.sin(t * REST_SWING.breatheRate) +
    REST_SWING.ripple * Math.sin(t * REST_SWING.rippleRate + REST_SWING.ripplePhase);
  const cycle = t % REST_SWING.twitchCycle;
  const twitch =
    cycle < REST_SWING.twitchLength
      ? REST_SWING.twitch *
        Math.sin((cycle * Math.PI) / REST_SWING.twitchLength) *
        Math.exp(-cycle * REST_SWING.twitchDecay)
      : 0;
  const foreFold = REST.foreFold + Math.min(0, breathe + twitch);
  return {
    foreFold,
    hindFold: foreFold - HIND_REST_GAP,
    foreFlex: REST_SWING.foreFlex * Math.sin(t * REST_SWING.flexRate),
    hindFlex: REST_SWING.hindFlex * Math.sin(t * REST_SWING.flexRate - REST_SWING.hindFlexPhase),
    foreSweep: SWEEP_FORE,
    hindSweep: SWEEP_HIND,
    bodyLift: 0,
    followRate: FOLLOW.rest,
  };
}

// The wing fields follow the target's rate. The body lift follows its own (`FOLLOW.bob`).
const WING_KEYS = [
  "foreFold",
  "hindFold",
  "foreFlex",
  "hindFlex",
  "foreSweep",
  "hindSweep",
] as const satisfies readonly Exclude<keyof MonarchPose, "bodyLift">[];

export class MonarchMotion {
  readonly pose: MonarchPose = { ...REST };
  private lastTime: number | undefined;
  private lastBeat = -Infinity;
  private lastKind: MonarchBeat["kind"] | undefined;
  private flapStart = -Infinity;
  private flutter: FlutterBurst | undefined;

  // Its own random source, so visual variety doesn't touch the engine's scheduling.
  constructor(private readonly rng: () => number = Math.random) {}

  private flutterTarget(now: number, beat: MonarchBeat): MonarchTarget {
    if (this.flutter?.start !== beat.beatStart) {
      const count = FLUTTER.minStrokes + Math.floor(this.rng() * FLUTTER.strokeChoices);
      const depth = (): number =>
        ((FLUTTER.minDepthDegrees + this.rng() * FLUTTER.depthRangeDegrees) * Math.PI) / 180;
      this.flutter = {
        start: beat.beatStart,
        duration: beat.beatDur,
        limits: Array.from({ length: count }, depth),
      };
    }
    const { start, duration, limits } = this.flutter;
    const travel = (elapsed: number): number => {
      if (elapsed <= 0 || elapsed >= duration) return 0;
      const progress = elapsed / duration;
      const stroke = progress * limits.length;
      const index = Math.floor(stroke);
      const pulse = Math.sin(Math.PI * (stroke - index)) ** 2;
      return limits[index]! * pulse * Math.sin(Math.PI * progress);
    };
    const fore = travel(now - start);
    const hind = travel(now - start - WING_DEFAULTS.lag);
    return {
      ...REST,
      foreFold: REST.foreFold - fore,
      hindFold: REST.hindFold - hind,
      foreFlex: -fore * FLUTTER.foreFlex,
      hindFlex: -hind * FLUTTER.hindFlex,
      followRate: FOLLOW.flutter,
    };
  }

  update(now: number, beat: MonarchBeat, effectsOn: boolean): MonarchPose {
    const dt = Math.min(0.05, Math.max(0, now - (this.lastTime ?? now)));
    if (this.lastTime !== undefined && now < this.lastTime) {
      this.lastBeat = -Infinity;
      this.lastKind = undefined;
      this.flapStart = -Infinity;
      this.flutter = undefined;
      Object.assign(this.pose, REST);
    }
    this.lastTime = now;
    if (!effectsOn) {
      this.lastBeat = beat.beatStart;
      this.lastKind = beat.kind;
      this.flapStart = -Infinity;
      this.flutter = undefined;
      Object.assign(this.pose, REST);
      return this.pose;
    }
    // The beat time also changes when a reset flap replaces a live beat. A flap that replaces
    // a beat from the same frame keeps its start time, so its kind is what marks it as new.
    if (beat.beatOn && (beat.beatStart !== this.lastBeat || beat.kind !== this.lastKind)) {
      this.lastBeat = beat.beatStart;
      this.lastKind = beat.kind;
      this.flapStart = beat.kind === "flutter" ? -Infinity : beat.beatStart;
    }
    const target =
      beat.beatOn && beat.kind === "flutter"
        ? this.flutterTarget(now, beat)
        : monarchTargets(
            now,
            now - this.flapStart < FLAP_SECONDS ? "flap" : "rest",
            this.flapStart,
          );
    const alpha = 1 - Math.exp(-dt * target.followRate);
    for (const key of WING_KEYS) this.pose[key] += (target[key] - this.pose[key]) * alpha;
    this.pose.bodyLift += (target.bodyLift - this.pose.bodyLift) * (1 - Math.exp(-dt * FOLLOW.bob));
    return this.pose;
  }
}
